import { useSyncExternalStore, useState, useCallback } from "react";
import type { TaskId } from "../../../../shared/types";
import {
  CATEGORY_BAR_COLORS,
  CATEGORY_LABELS,
  STATUS_LABELS,
  STATUS_CYCLE,
} from "../../../../shared/types";
import { Overlay } from "../../../../ui/atoms/Overlay";
import { ModalActions, type ModalActionMode } from "../../../../ui/molecules/ModalActions";
import { TaskItemDetails } from "../../../tasks/components/task-item/TaskItemDetails";
import * as taskStore from "../../../tasks/store/task-store";

interface TaskQuickViewModalProps {
  readonly taskId: TaskId;
  readonly onClose: () => void;
  readonly readOnly?: boolean;
}

export function TaskQuickViewModal({
  taskId,
  onClose,
  readOnly,
}: TaskQuickViewModalProps) {
  const allTasks = useSyncExternalStore(
    taskStore.subscribe,
    taskStore.getSnapshot,
  );
  const task = allTasks.find((t) => t.id === taskId);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleTitleClick = useCallback(() => {
    if (!task) return;
    setTitleDraft(task.title);
    setIsEditingTitle(true);
    setHasChanges(false);
  }, [task]);

  const handleTitleCommit = useCallback(() => {
    const trimmed = titleDraft.trim();
    if (trimmed && task && trimmed !== task.title) {
      taskStore.updateTask(task.id, { title: trimmed });
    }
    setIsEditingTitle(false);
    setHasChanges(false);
  }, [titleDraft, task]);

  const handleTitleChange = useCallback((value: string) => {
    setTitleDraft(value);
    setHasChanges(task ? value.trim() !== task.title : false);
  }, [task]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleTitleCommit();
      }
      if (e.key === "Escape") {
        setIsEditingTitle(false);
        setHasChanges(false);
      }
    },
    [handleTitleCommit],
  );

  const handleCycleStatus = useCallback(() => {
    if (!task) return;
    taskStore.cycleStatus(task.id);
  }, [task]);

  const handleDelete = useCallback(() => {
    if (!task) return;
    setIsDeleting(true);
    taskStore.deleteTask(task.id);
    setTimeout(() => onClose(), 100);
  }, [task, onClose]);

  if (!task) {
    onClose();
    return null;
  }

  const nextStatus = STATUS_CYCLE[task.status];
  const mode: ModalActionMode = readOnly ? "view" : isEditingTitle ? "edit" : "view";

  return (
    <Overlay open onClose={onClose}>
      <div className="bg-surface border border-border rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="relative px-5 pt-5 pb-3 shrink-0">
          <div
            className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl ${CATEGORY_BAR_COLORS[task.category]}`}
          />
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {!readOnly && isEditingTitle ? (
                <input
                  type="text"
                  value={titleDraft}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onBlur={handleTitleCommit}
                  onKeyDown={handleTitleKeyDown}
                  className="w-full text-base font-semibold text-ink bg-transparent border-b border-accent outline-none leading-tight"
                  autoFocus
                />
              ) : readOnly ? (
                <p className="text-base font-semibold text-ink leading-tight">
                  {task.title}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleTitleClick}
                  className="text-base font-semibold text-ink leading-tight text-left hover:text-accent transition-colors w-full"
                >
                  {task.title}
                </button>
              )}
              <div className="flex items-center gap-2 mt-1">
                {readOnly ? (
                  <span className="text-sm font-medium text-ink-muted">
                    {STATUS_LABELS[task.status]}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleCycleStatus}
                    className="text-sm font-medium text-ink-muted hover:text-accent transition-colors"
                  >
                    {STATUS_LABELS[task.status]}
                  </button>
                )}
                <span className="text-[10px] text-ink-faint">
                  {CATEGORY_LABELS[task.category]}
                </span>
                {task.recurrence && (
                  <span className="text-[10px] text-ink-faint">↻</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-md text-ink-faint hover:text-ink hover:bg-surface-alt transition-colors"
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-2 pb-4 flex-1 overflow-y-auto">
          <TaskItemDetails
            task={task}
            onUpdate={taskStore.updateTask}
            onAddSubtask={taskStore.addSubtask}
            onToggleSubtask={taskStore.toggleSubtask}
            onDeleteSubtask={taskStore.deleteSubtask}
          />
        </div>

        {/* Actions Footer */}
        {!readOnly && (
          <div className="px-5 pb-5 flex flex-col gap-2 border-t border-border/30 pt-4 shrink-0">
            <ModalActions
              mode={mode}
              hasChanges={hasChanges}
              isSaving={false}
              isValid={hasChanges}
              onCancel={onClose}
              onSave={handleTitleCommit}
              onEdit={handleTitleClick}
              customPrimaryAction={
                isEditingTitle
                  ? undefined
                  : {
                      label: `Passer à : ${STATUS_LABELS[nextStatus]}`,
                      onClick: handleCycleStatus,
                      disabled: false,
                      variant: "primary",
                    }
              }
            />

            {/* Delete button séparé */}
            {!isEditingTitle && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="w-full py-2 text-xs font-medium text-danger border border-danger/30 rounded-md hover:bg-danger/10 focus:outline-none transition-colors disabled:opacity-50 min-h-[44px] touch-feedback"
              >
                {isDeleting ? "Suppression..." : "Supprimer"}
              </button>
            )}
          </div>
        )}
      </div>
    </Overlay>
  );
}
